import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Popover from '@mui/material/Popover';
import CloseIcon from '@mui/icons-material/Close';

// Icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ReviewsIcon from '@mui/icons-material/Reviews';
import GetAppIcon from '@mui/icons-material/GetApp';
import NotesIcon from '@mui/icons-material/Notes';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import TimerIcon from '@mui/icons-material/Timer';
import PersonIcon from '@mui/icons-material/Person';
import MessageIcon from '@mui/icons-material/Message';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { formatDistanceToNow } from 'date-fns';

import useConversationDensity from './useConversationDensity';

const ConversationSidebar = ({
  isCollapsed,
  onToggleCollapse,
  conversation,
  clientInfo,
  currentMode,
  autoForwardEnabled,
  onToggleAutoForward,
  onGenerateSummary,
  onViewSummary,
  hasSummary = false,
  onRequestReview,
  onExportTranscript,
  onViewCareNotes,
  onProposeGoal,
  onInitiateAssessment,
  onOpenSettings,
  onOpenDelaySettings,
  aiStatus,
  isProcessing,
  careNoteCount,
  styles = {},
  responsive = {},
  isMobileOpen = false,
  onMobileClose
}) => {
  const { density, focusMode } = useConversationDensity();
  const [localAutoForward, setLocalAutoForward] = useState(autoForwardEnabled);
  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);

  useEffect(() => {
    setLocalAutoForward(autoForwardEnabled);
  }, [autoForwardEnabled]);

  const handleAutoForwardChange = async (event) => {
    const newValue = event.target.checked;
    setLocalAutoForward(newValue);
    if (onToggleAutoForward) {
      await onToggleAutoForward(newValue);
    }
  };

  // Calculate conversation stats
  const messageCount = conversation?.messages?.length || 0;
  const duration = conversation?.createdAt 
    ? formatDistanceToNow(new Date(conversation.createdAt), { addSuffix: false })
    : 'Just started';
  const lastActivity = conversation?.messages?.[messageCount - 1]?.timestamp
    ? formatDistanceToNow(new Date(conversation.messages[messageCount - 1].timestamp), { addSuffix: true })
    : 'No messages yet';

  // Sidebar content component (shared between desktop and mobile)
  const SidebarContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile header with close button */}
      {responsive.shouldShowDrawerSidebar && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid rgba(0,0,0,0.08)'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Conversation Details
          </Typography>
          <IconButton onClick={onMobileClose} size="small" aria-label="Close sidebar">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <Box sx={styles.sidebarContent}>
        {/* Desktop collapse button */}
        {!responsive.shouldShowDrawerSidebar && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Tooltip title="Collapse sidebar">
              <IconButton onClick={onToggleCollapse} size="small" aria-label="Collapse sidebar">
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Conversation Info Card */}
        <Box sx={styles.sidebarCard}>
          <Typography sx={styles.sidebarCardTitle}>
            <InfoIcon sx={{ fontSize: 18 }} />
            Conversation Info
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                {clientInfo?.name || 'Client'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MessageIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                {messageCount} messages
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Duration: {duration}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Active {lastActivity}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* AI Assistant Card - Only show in assisted mode */}
        {currentMode === 'assisted' && (
          <Box sx={styles.sidebarCard}>
            <Typography sx={styles.sidebarCardTitle}>
              <SmartToyIcon sx={{ fontSize: 18 }} />
              AI Assistant
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={aiStatus || 'Ready'}
                  size="small"
                  icon={isProcessing ? <CircularProgress size={12} /> : <FlashOnIcon />}
                  sx={{
                    backgroundColor: isProcessing ? '#FFF3E0' : '#E8F5E9',
                    color: isProcessing ? '#F57C00' : '#2E7D32'
                  }}
                />
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={localAutoForward}
                    onChange={handleAutoForwardChange}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Auto-Forward AI Suggestions
                  </Typography>
                }
              />
              
              {localAutoForward && (
                <Typography variant="caption" sx={{ color: '#64748b', mt: -1 }}>
                  AI suggestions will be sent automatically after review
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Quick Actions Card */}
        <Box sx={styles.sidebarCard}>
          <Typography sx={styles.sidebarCardTitle}>
            <FlashOnIcon sx={{ fontSize: 18 }} />
            Quick Actions
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {hasSummary ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<SummarizeIcon />}
                  onClick={onViewSummary}
                  sx={{ 
                    justifyContent: 'flex-start', 
                    textTransform: 'none',
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    '&:hover': {
                      borderColor: '#45a049',
                      backgroundColor: 'rgba(76, 175, 80, 0.04)'
                    }
                  }}
                >
                  View Summary
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<SummarizeIcon />}
                  onClick={onGenerateSummary}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  Regenerate Summary
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<SummarizeIcon />}
                onClick={onGenerateSummary}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                Generate Summary
              </Button>
            )}
            
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<ReviewsIcon />}
              onClick={onRequestReview}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Request Review
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<GetAppIcon />}
              onClick={onExportTranscript}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Export Transcript
            </Button>
            
            {careNoteCount > 0 && (
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<NotesIcon />}
                onClick={onViewCareNotes}
                sx={{ 
                  justifyContent: 'flex-start', 
                  textTransform: 'none',
                  borderColor: '#9C27B0',
                  color: '#9C27B0',
                  '&:hover': {
                    borderColor: '#7B1FA2',
                    backgroundColor: 'rgba(156, 39, 176, 0.04)'
                  }
                }}
              >
                View Care Notes ({careNoteCount})
              </Button>
            )}
          </Box>
        </Box>

        {/* Tools Card */}
        <Box sx={styles.sidebarCard}>
          <Typography sx={styles.sidebarCardTitle}>
            <SettingsIcon sx={{ fontSize: 18 }} />
            Tools
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="text"
              size="small"
              fullWidth
              startIcon={<AddTaskIcon />}
              onClick={onProposeGoal}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Propose Goal
            </Button>
            
            <Button
              variant="text"
              size="small"
              fullWidth
              startIcon={<AssessmentIcon />}
              onClick={onInitiateAssessment}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Initiate Assessment
            </Button>
            
            <Divider sx={{ my: 1 }} />
            
            <Button
              variant="text"
              size="small"
              fullWidth
              startIcon={<SettingsIcon />}
              onClick={onOpenSettings}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Conversation Settings
            </Button>
            
            <Button
              variant="text"
              size="small"
              fullWidth
              startIcon={<TimerIcon />}
              onClick={onOpenDelaySettings}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              Response Delays
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Reusable rail button (tooltip + icon, vertical rail layout)
  const RailButton = ({ title, icon, onClick, color, badge, ariaLabel }) => (
    <Tooltip title={title} placement="right">
      <IconButton
        onClick={onClick}
        size="small"
        aria-label={ariaLabel || title}
        sx={{ color: color || 'text.secondary' }}
      >
        {badge != null && badge > 0 ? (
          <Box sx={{ position: 'relative', display: 'flex' }}>
            {icon}
            <Chip
              label={badge}
              size="small"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                height: 16,
                fontSize: '10px',
                backgroundColor: '#9C27B0',
                color: '#fff'
              }}
            />
          </Box>
        ) : (
          icon
        )}
      </IconButton>
    </Tooltip>
  );

  // Focus mode: the sidebar is hidden entirely so the transcript can go
  // full-width. (Desktop only — mobile keeps its drawer behaviour.)
  if (focusMode && !responsive.shouldShowDrawerSidebar) {
    return null;
  }

  // Compact mode: collapse the labelled sidebar into a ~48px icon rail.
  // Reuses every existing action handler — only the presentation changes.
  if (density === 'compact' && !responsive.shouldShowDrawerSidebar) {
    return (
      <Box sx={{ ...styles.sidebar, ...styles.sidebarCollapsed }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 1.5,
            gap: 1.5,
            height: '100%',
            overflowY: 'auto'
          }}
        >
          <RailButton
            title="Conversation Info"
            ariaLabel="Conversation info"
            icon={<InfoIcon />}
            onClick={(e) => setInfoPopoverAnchor(e.currentTarget)}
          />

          {currentMode === 'assisted' && (
            <Tooltip title="AI Assistant" placement="right">
              <IconButton size="small" aria-label="AI Assistant" sx={{ color: 'text.secondary' }}>
                <SmartToyIcon color={isProcessing ? 'primary' : 'inherit'} />
              </IconButton>
            </Tooltip>
          )}

          <Divider sx={{ width: '60%' }} />

          {hasSummary ? (
            <>
              <RailButton
                title="View Summary"
                ariaLabel="View summary"
                icon={<SummarizeIcon />}
                color="#4CAF50"
                onClick={onViewSummary}
              />
              <RailButton
                title="Regenerate Summary"
                ariaLabel="Regenerate summary"
                icon={<SummarizeIcon />}
                onClick={onGenerateSummary}
              />
            </>
          ) : (
            <RailButton
              title="Generate Summary"
              ariaLabel="Generate summary"
              icon={<SummarizeIcon />}
              onClick={onGenerateSummary}
            />
          )}

          <RailButton
            title="Request Review"
            ariaLabel="Request review"
            icon={<ReviewsIcon />}
            onClick={onRequestReview}
          />

          <RailButton
            title="Export Transcript"
            ariaLabel="Export transcript"
            icon={<GetAppIcon />}
            onClick={onExportTranscript}
          />

          {careNoteCount > 0 && (
            <RailButton
              title={`View Care Notes (${careNoteCount})`}
              ariaLabel={`View ${careNoteCount} care notes`}
              icon={<NotesIcon />}
              color="#9C27B0"
              badge={careNoteCount}
              onClick={onViewCareNotes}
            />
          )}

          <Divider sx={{ width: '60%' }} />

          <RailButton
            title="Propose Goal"
            ariaLabel="Propose goal"
            icon={<AddTaskIcon />}
            onClick={onProposeGoal}
          />

          <RailButton
            title="Initiate Assessment"
            ariaLabel="Initiate assessment"
            icon={<AssessmentIcon />}
            onClick={onInitiateAssessment}
          />

          <Divider sx={{ width: '60%' }} />

          <RailButton
            title="Conversation Settings"
            ariaLabel="Conversation settings"
            icon={<SettingsIcon />}
            onClick={onOpenSettings}
          />

          <RailButton
            title="Response Delays"
            ariaLabel="Response delays"
            icon={<TimerIcon />}
            onClick={onOpenDelaySettings}
          />
        </Box>

        {/* Info popover — surfaces the stats that the full card would show */}
        <Popover
          open={Boolean(infoPopoverAnchor)}
          anchorEl={infoPopoverAnchor}
          onClose={() => setInfoPopoverAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box sx={{ p: 2, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ ...styles.sidebarCardTitle, mb: 0.5 }}>
              <InfoIcon sx={{ fontSize: 18 }} />
              Conversation Info
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                {clientInfo?.name || 'Client'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MessageIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                {messageCount} messages
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Duration: {duration}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: '#64748b' }} />
              <Typography variant="body2" sx={{ color: '#475569' }}>
                Active {lastActivity}
              </Typography>
            </Box>
          </Box>
        </Popover>
      </Box>
    );
  }

  // Collapsed sidebar for desktop
  if (isCollapsed && !responsive.shouldShowDrawerSidebar) {
    return (
      <Box sx={{ ...styles.sidebar, ...styles.sidebarCollapsed }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          py: 2, 
          gap: 2 
        }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton onClick={onToggleCollapse} size="small" aria-label="Expand sidebar">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>

          <Divider sx={{ width: '60%' }} />

          {/* Collapsed icons with tooltips */}
          <Tooltip title="Conversation Info" placement="right">
            <IconButton size="small" aria-label="Conversation info">
              <InfoIcon />
            </IconButton>
          </Tooltip>

          {currentMode === 'assisted' && (
            <Tooltip title="AI Assistant" placement="right">
              <IconButton size="small" aria-label="AI Assistant">
                <SmartToyIcon color={isProcessing ? 'primary' : 'inherit'} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Generate Summary" placement="right">
            <IconButton onClick={onGenerateSummary} size="small" aria-label="Generate summary">
              <SummarizeIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Request Review" placement="right">
            <IconButton onClick={onRequestReview} size="small" aria-label="Request review">
              <ReviewsIcon />
            </IconButton>
          </Tooltip>

          {careNoteCount > 0 && (
            <Tooltip title={`${careNoteCount} Care Notes`} placement="right">
              <IconButton onClick={onViewCareNotes} size="small" aria-label={`View ${careNoteCount} care notes`}>
                <Box sx={{ position: 'relative' }}>
                  <NotesIcon />
                  <Chip
                    label={careNoteCount}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      height: 16,
                      fontSize: '10px',
                      backgroundColor: '#9C27B0',
                      color: '#fff'
                    }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  }

  // Mobile/Tablet drawer
  if (responsive.shouldShowDrawerSidebar) {
    return (
      <SwipeableDrawer
        anchor="left"
        open={isMobileOpen}
        onClose={onMobileClose}
        onOpen={() => {}}
        sx={{
          '& .MuiDrawer-paper': {
            width: responsive.isMobile ? '85%' : '320px',
            maxWidth: '400px',
            boxSizing: 'border-box'
          }
        }}
      >
        <SidebarContent />
      </SwipeableDrawer>
    );
  }

  // Desktop sidebar
  return (
    <Box sx={styles.sidebar}>
      <SidebarContent />
    </Box>
  );
};

export default ConversationSidebar;